import { useEffect, type FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { twJoin } from 'tailwind-merge';

import { useDynamicSaga } from '@/domains/core/applicationState/hooks/useDynamicSaga';
import { ListingCardImage } from '@/domains/productDiscovery/CommercialAnimation/components/ListingCampaignCards/components/ListingCardImage';
import type { Type } from '@/domains/productDiscovery/CommercialAnimation/interfaces/bannerCampaign';
import { useMerchandizingBanners } from '@/domains/productDiscovery/Listings/modules/MerchandizingBanners/hooks/useMerchandizingBanners';
import { initMerchandizingBanners } from '@/domains/productDiscovery/Listings/modules/MerchandizingBanners/MerchandizingBanners.actions';
import { rootSaga } from '@/domains/productDiscovery/Listings/modules/MerchandizingBanners/MerchandizingBanners.saga';
import { BannerCampaignAdvertiserLabel } from '@/productDiscovery/CommercialAnimation/components/AdvertiserInformation/BannerCampaignAdvertiserLabel';
import { useCategoryIds } from '@/productDiscovery/CommercialAnimation/hooks/useCategoryIds';
import { PartnerAdBanner } from '@/sellerAdsManagement/PartnerAdBannerCampaign/components/PartnerAdBanner';
import { spaceSlugType } from '@/sellerAdsManagement/PartnerAdBannerCampaign/interfaces/spaceSlugTypes';

export interface ListingCampaignCardProps {
  type: Type;
}

export const ListingCampaignCard: FunctionComponent<
  ListingCampaignCardProps
> = ({ type }) => {
  useDynamicSaga('merchandizingManagerSaga', rootSaga);
  const { isFetching, campaigns } = useMerchandizingBanners(type);
  const campaign = campaigns && campaigns?.length > 0 ? campaigns[0] : null;
  const categoryIds = useCategoryIds();

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(initMerchandizingBanners(type, categoryIds));
  }, [dispatch, type, categoryIds]);

  const twCardClasses = 'block w-full h-full rounded-card';

  if (isFetching || !campaign) {
    return <div className={twCardClasses} />;
  }

  return campaign.isPartnerAd ? (
    <PartnerAdBanner
      partnerAdBannerCampaign={campaign}
      bannerTypeTracking={`${type}_banner`}
      spaceSlug={spaceSlugType.LISTING_BANNER}
    />
  ) : (
    <>
      {campaign.sponsor && campaign.advertiser && (
        <BannerCampaignAdvertiserLabel
          sponsor={campaign.sponsor}
          advertiser={campaign.advertiser}
          userSegments={campaign.userSegments}
          spaceSlug={`${type}_banner`}
        />
      )}
      <ListingCardImage
        campaign={campaign}
        className={twJoin(twCardClasses, 'object-cover')}
        bannerType={type === 'bottom' ? 'bottom' : 'middle'}
      />
    </>
  );
};

ListingCampaignCard.displayName = 'ListingCampaignCard';
